import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitEmpty,
    KitEntityCard,
    KitFab,
    KitField,
    KitSearch,
    KitSelect,
    KitStickyToolbar,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { APP_BAR_OFFSET } from '../layout/mobileShell';
import { INVENTORY_STATUS } from '../../pages/inventory/inventoryStatus';

/**
 * Lista de inventários no shell mobile — `prototipos-mobile/inventario/lista.html`.
 * Só apresentação: busca, filtro de status e navegação vêm de `pages/inventory/List.js`.
 */

const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const CalendarIcon = protoIcon('calendar');
const PackageIcon = protoIcon('package');
const ClipIcon = protoIcon('clipcheck');

const PAGE_SIZE = 20;
const STRIPE = '#8b5cf6';

// "15/10" (data de criação)
function shortDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function InventoryCard({ row, onOpen }) {
    const status = INVENTORY_STATUS[row.status] || INVENTORY_STATUS.draft;
    return (
        <KitEntityCard stripeColor={STRIPE} onClick={onOpen ? () => onOpen(row) : undefined}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: '-.01em' }}>Inventário #{row.code}</Box>
                <KitPill tone={status.tone}>{status.label}</KitPill>
            </Box>
            <Box sx={{ m: '8px 0 10px' }}>
                <Box sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.4, color: row.description ? 'text.primary' : 'text.disabled' }}>{row.description || 'Sem descrição'}</Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><CalendarIcon size={13} />{shortDate(row.created_at)}</Box>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><PackageIcon size={13} />{row.items_count} {row.items_count === 1 ? 'item' : 'itens'}</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', mt: '12px', pt: '10px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '-.02em' }} />
                <Box component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {row.scope === 'partial' ? 'Parcial' : 'Completo'}{row.location_label ? ` · ${row.location_label}` : ''}
                </Box>
            </Box>
        </KitEntityCard>
    );
}

export default function MobileInventoryList({
    inventories,
    loading,
    search,
    onSearchChange,
    statusFilter,
    onStatusChange,
    canCreate,
    onOpen,
    onNew,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [limit, setLimit] = useState(PAGE_SIZE);
    const visible = inventories.slice(0, limit);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={APP_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Buscar…"
                    inputProps={{ 'aria-label': 'Buscar inventário', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton
                    aria-label="Filtros"
                    onClick={() => setFiltersOpen(true)}
                    sx={statusFilter ? { color: 'primary.main', borderColor: 'primary.main' } : undefined}
                >
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <Box sx={{ mt: '12px' }} />

            {loading && inventories.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={140} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && inventories.length === 0 && (
                <KitEmpty icon={ClipIcon} title="Nenhum inventário encontrado">
                    Ajuste a busca ou o filtro para ver outros inventários.
                </KitEmpty>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {visible.map((row) => <InventoryCard key={row.id} row={row} onOpen={onOpen} />)}
            </Box>

            {inventories.length > limit && (
                <KitButton block sx={{ mt: '14px' }} onClick={() => setLimit((l) => l + PAGE_SIZE)}>Carregar mais</KitButton>
            )}

            {canCreate && <KitFab label="Novo" icon={PlusIcon} onClick={onNew} />}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={() => { onStatusChange(''); setFiltersOpen(false); }}>Limpar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => setFiltersOpen(false)}>Aplicar</KitButton>
                    </>
                )}
            >
                <KitField label="Status">
                    <KitSelect value={statusFilter} onChange={(e) => onStatusChange(e.target.value)} aria-label="Status">
                        <option value="">Todos</option>
                        {Object.entries(INVENTORY_STATUS).map(([value, status]) => (
                            <option key={value} value={value}>{status.label}</option>
                        ))}
                    </KitSelect>
                </KitField>
            </MobileSheet>
        </Box>
    );
}
